// ============================================================
// TL4K Socket.IO — Centralized SocketService
// ============================================================
// Single shared SocketService class for all TL4K apps.
// Replaces 7 duplicate implementations across the monorepo.
//
// Usage:
//   const socket = createSocketService({ logPrefix: 'MyApp' });
//   socket.joinRoom('ABC123');
//   socket.on('my_event', handler);
//   socket.emit('my_event', data);
// ============================================================

import { io, type Socket } from 'socket.io-client';
import { resolveCollabUrl, getDefaultSocketOptions } from './config';
import { buildSocketAuthProvider, getAuthToken } from './auth';
import { clearStudentToken, getStudentTokenKind } from '../auth/studentToken';
import type { SocketAuthConfig, TlkNamespace } from './types';

export interface CreateSocketServiceOptions {
  /**
   * Prefix for console logs. E.g. 'QNS' → '[QNS Socket]'.
   * @default 'SOCKET'
   */
  logPrefix?: string;

  /**
   * If true, log all received socket events via socket.onAny().
   * @default false
   */
  trace?: boolean;

  /**
   * Called on every successful connect (including reconnects).
   * Use this to re-join rooms after a reconnection.
   */
  onConnect?: () => void;

  /**
   * Called on disconnect.
   */
  onDisconnect?: (reason: string) => void;

  /**
   * Socket.IO namespace. Default: '/' (root namespace).
   * Use '/presentations' for teacher presentation namespace.
   */
  namespace?: TlkNamespace;

  /**
   * Auth configuration for Socket.IO handshake.
   * If not provided, attempts to auto-detect JWT from localStorage.
   */
  auth?: SocketAuthConfig;

  /**
   * Override transports. Default: ['polling'] in dev, ['polling', 'websocket'] in prod.
   */
  transports?: string[];

  /**
   * Override reconnection attempts. Default: 12.
   */
  reconnectionAttempts?: number;

  /**
   * Override reconnection delay. Default: 1500ms.
   */
  reconnectionDelay?: number;
}

/**
 * Creates and manages a Socket.IO connection to the TL4K collaboration service.
 *
 * Features:
 * - Automatic URL resolution (dev proxy vs production)
 * - JWT auth from localStorage
 * - Room management (join/leave)
 * - Event listener management
 * - Reconnection handling
 * - Trace mode for debugging
 */
export class SocketService {
  private socket: Socket | null = null;
  /**
   * Listener dell'app, per evento. Set: lo stesso handler non si registra due volte.
   * Si attaccano UNA volta per istanza di socket (alla creazione, o subito in on()):
   * socket.io-client riusa la stessa istanza tra le riconnessioni e i listener restano,
   * quindi riattaccarli a ogni 'connect' li moltiplicava (comandi docente eseguiti N volte).
   */
  private listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();
  private onConnectCallbacks: Array<() => void> = [];
  private onDisconnectCallbacks: Array<(reason: string) => void> = [];
  /** WP-13e: il server ha tolto l'identita' studente (revoca o join_room rifiutato). */
  private identityLostCallbacks: Array<(reason: 'revoked' | 'identity_required') => void> = [];
  private lastConnectErrorWallMs = 0;
  /** Phase 2a: tracks PINs to re-emit join_room on every reconnect. */
  private joinedRooms: Set<string> = new Set();
  /** Phase 2a: tracks (pin, participantId) tuples for re-registration on reconnect. */
  private registeredPresence: Map<string, string> = new Map();

  // "Stato dei lavori": battito di vita emesso a ogni presenza registrata.
  // Non e' il ping di latenza (rt_ping, una volta per connessione): dice al
  // server "sono ancora qui e sto lavorando", cosi' il pannello del docente
  // distingue il fermo-proposito dal bloccato. Il server lo confronta con la
  // soglia che il docente ha scelto: 30 s qui stanno bene con una soglia di
  // 3 minuti, che lascia margine per due battiti persi.
  private static readonly STATO_LAVORI_BEAT_MS = 30_000;
  private statoLavoriTimer: ReturnType<typeof setInterval> | null = null;
  private readonly logPrefix: string;
  private readonly trace: boolean;
  private readonly namespace: TlkNamespace;
  private authConfig?: SocketAuthConfig;
  private readonly transports?: string[];
  private readonly reconnectionAttempts?: number;
  private readonly reconnectionDelay?: number;

  constructor(options: CreateSocketServiceOptions = {}) {
    this.logPrefix = options.logPrefix ?? 'SOCKET';
    this.trace = options.trace ?? false;
    this.namespace = options.namespace ?? '/';
    this.authConfig = options.auth;
    this.transports = options.transports;
    this.reconnectionAttempts = options.reconnectionAttempts;
    this.reconnectionDelay = options.reconnectionDelay;

    if (options.onConnect) {
      this.onConnectCallbacks.push(options.onConnect);
    }
    if (options.onDisconnect) {
      this.onDisconnectCallbacks.push(options.onDisconnect);
    }
  }

  // ---- Connection management ----

  /**
   * Connect to the collaboration service.
   * Idempotent: does nothing if already connected.
   *
   * IMPORTANT: if an instance already exists it is NEVER orphaned. A second
   * io() while the first is still connecting/reconnecting would open a second
   * polling loop (and, if repeated, hundreds of polling requests →
   * ERR_INSUFFICIENT_RESOURCES in the browser). Only a truly dead socket
   * (connect failed AND reconnection exhausted) is recreated.
   */
  connect(): void {
    const existing = this.socket;
    if (existing) {
      // socket.io-client: `active` is true while connecting or reconnecting.
      const isActive =
        existing.connected ||
        (existing as unknown as { active?: boolean }).active === true;
      if (isActive) return;
      // Dead socket (reconnectionAttempts exhausted): recycle it instead of
      // stacking a new instance on top of it.
      try {
        existing.removeAllListeners();
        existing.disconnect();
      } catch (err) {
        console.warn(`[${this.logPrefix} Socket] cleanup of dead socket failed:`, err);
      }
      this.socket = null;
    }

    const resolved = resolveCollabUrl();
    const tag = `[${this.logPrefix} Socket]`;

    const socketOptions = getDefaultSocketOptions({
      transports: this.transports,
      reconnectionAttempts: this.reconnectionAttempts,
      reconnectionDelay: this.reconnectionDelay,
    });

    // Build namespace URL: append namespace path to base URL
    // For default namespace ('/'), use base URL only
    // For other namespaces, append namespace to URL
    let url = resolved.url;
    let path = socketOptions.path;

    if (this.namespace !== '/') {
      // For named namespaces, append to URL
      // Socket.IO will connect to: url + namespace
      url = resolved.url;
      // Path stays the same — namespace is part of the Socket.IO protocol
    }

    // Build auth handshake
    // WP-13e: auth come funzione, ricalcolata a ogni (ri)connessione: dopo un riscatto o un nuovo ingresso
    // la riconnessione porta il token studente corrente.
    const auth = buildSocketAuthProvider(() => this.authConfig);

    this.trace && console.warn(`${tag} [DIAG] io(${JSON.stringify(url)}, { path: ${JSON.stringify(path)}, transports: ${JSON.stringify(socketOptions.transports)} })`);
    this.socket = io(url, {
      ...socketOptions,
      path,
      auth,
      // Namespace: Socket.IO handles this via the URL path
      // For default namespace, no extra path needed
      // For named namespaces, we use the namespace in the URL
    });

    // If using a named namespace, we need to connect to it explicitly
    if (this.namespace !== '/') {
      // Reconnect with namespace
      this.socket.disconnect();
      this.socket = io(`${url}${this.namespace}`, {
        ...socketOptions,
        path,
        auth,
      });
    }

    // Listener dell'app sulla nuova istanza, una volta sola: restano attaccati
    // anche attraverso le riconnessioni (stessa istanza).
    for (const [event, callbacks] of this.listeners) {
      for (const callback of callbacks) this.socket.on(event, callback);
    }

    this.socket.on('connect', () => {
      this.trace && console.log(`${tag} Connected:`, this.socket?.id);
      // Phase 2a: re-emit join_room for every previously joined room.
      // Server keeps no per-socket state across disconnects, so without this students
      // miss every subsequent broadcast after a network blip.
      for (const pin of this.joinedRooms) {
        this.socket?.emit('join_room', { pin });
        this.trace && console.log(`${tag} re-join_room on reconnect:`, pin);
      }
      // Phase 2a: re-register presence for tracked (pin, participantId) tuples.
      for (const [pin, participantId] of this.registeredPresence) {
        this.socket?.emit('register_presence', { pin, participant_id: participantId });
      }
      this.onConnectCallbacks.forEach((cb) => cb());
    });

    this.socket.on('disconnect', (reason: string) => {
      this.trace && console.log(`${tag} Disconnected:`, reason);
      // WP-13e: 'io server disconnect' con un token studente = revoca (rimozione, rientro da altro
      // dispositivo, generazione vecchia). socket.io NON riconnette da solo e un nuovo tentativo con lo
      // stesso token fallirebbe: si cancella il token e si avvisa, cosi' l'app torna all'ingresso.
      // Solo sul namespace principale e solo per un token participant: un altro namespace (es.
      // /presentations) puo' chiudere il proprio socket per motivi suoi, e la proposal non e' un'identita'.
      if (reason === 'io server disconnect' && this.namespace === '/' && getStudentTokenKind() === 'participant') {
        clearStudentToken();
        this.identityLostCallbacks.forEach((cb) => cb('revoked'));
      }
      this.onDisconnectCallbacks.forEach((cb) => cb(reason));
    });

    // WP-13e: il server rifiuta join_room senza identita' valida (STUDENT_TOKEN_MODE=enforce).
    // Niente ritentativi in ciclo: la stanza esce dal tracking (la riconnessione non la riemette)
    // e, se c'era un token, e' scaduto o non valido per quella radice: si cancella.
    this.socket.on('join_room_rejected', (data?: { reason?: string; pin?: string }) => {
      if (data?.reason !== 'identity_required') return;
      if (data.pin) this.joinedRooms.delete(String(data.pin).trim().toUpperCase());
      else this.joinedRooms.clear();
      if (this.namespace !== '/') return;
      if (getStudentTokenKind() === 'participant') clearStudentToken();
      this.identityLostCallbacks.forEach((cb) => cb('identity_required'));
    });

    this.socket.on('connect_error', (err: { message: string }) => {
      const now = Date.now();
      const throttleMs = 30000;
      if (this.trace) {
        console.warn(`${tag} Connect error:`, err.message);
      } else if (now - this.lastConnectErrorWallMs >= throttleMs) {
        this.lastConnectErrorWallMs = now;
        console.warn(
          `${tag} Collaboration service unreachable. Retrying in background.`,
        );
      }
    });

    // Trace mode: log all events
    if (this.trace) {
      this.socket.onAny((event: string, ...args: unknown[]) => {
        console.log(`${tag} Event:`, event, args.length > 0 ? args[0] : '');
      });
    }
  }

  /**
   * Disconnect from the collaboration service.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    // Phase 2a: explicit disconnect clears tracking — caller doesn't want auto-rejoin on next connect().
    this.joinedRooms.clear();
    this.registeredPresence.clear();
    // Nessuna presenza tracciata = nessun battito da mandare: fermarlo evita
    // un timer che sopravvive alla sessione e invia pacchetti a vuoto.
    this.stopStatoLavoriBeat();
  }

  /**
   * Check if currently connected.
   */
  isConnected(): boolean {
    return this.socket?.connected === true;
  }

  /**
   * Get the underlying Socket.IO instance (for advanced usage).
   * @internal Prefer using the public API methods instead.
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  // ---- Room management ----

  /**
   * Join a session room by PIN.
   * Emits immediately if connected, or queues for next connect.
   */
  joinRoom(pin: string): void {
    if (!this.socket) this.connect();
    const normalized = String(pin ?? '').trim().toUpperCase();
    if (!normalized) return;
    // Phase 2a: record the room before emit so reconnect handler can replay it.
    this.joinedRooms.add(normalized);
    const doEmit = () => {
      this.socket?.emit('join_room', { pin: normalized });
      this.trace && console.log(`[${this.logPrefix} Socket] join_room:`, normalized);
    };
    if (this.socket?.connected) {
      doEmit();
    } else {
      this.socket?.once('connect', doEmit);
    }
  }

  /**
   * Leave a session room by PIN.
   */
  /**
   * Leave a session room by PIN.
   * Behavior:
   * - Removes the pin from local tracking (so reconnect won't auto-rejoin).
   * - If currently connected, emits `leave_room` to the server.
   * - If disconnected: tracking is still cleared; the server-side room membership
   *   will be cleaned up when the underlying socket times out / disconnects. The
   *   client will NOT re-join on the next reconnect, which is the intended
   *   semantics for an explicit leave.
   */
  leaveRoom(pin: string): void {
    const normalized = String(pin ?? '').trim().toUpperCase();
    if (!normalized) return;
    this.joinedRooms.delete(normalized);
    this.registeredPresence.delete(normalized);
    if (this.registeredPresence.size === 0) this.stopStatoLavoriBeat();
    if (!this.socket?.connected) return;
    this.socket.emit('leave_room', { pin: normalized });
    this.trace && console.log(`[${this.logPrefix} Socket] leave_room:`, normalized);
  }

  /**
   * Register presence in a session room.
   * Ensures the socket is in the room before emitting.
   */
  /** Avvia il battito una sola volta, se non e' gia' in corso. */
  private startStatoLavoriBeat(): void {
    if (this.statoLavoriTimer) return;
    this.statoLavoriTimer = setInterval(() => {
      if (!this.socket?.connected) return;
      for (const [pin, participantId] of this.registeredPresence) {
        this.socket.emit('stato_lavori_beat', { pin, participant_id: participantId });
      }
    }, SocketService.STATO_LAVORI_BEAT_MS);
  }

  private stopStatoLavoriBeat(): void {
    if (!this.statoLavoriTimer) return;
    clearInterval(this.statoLavoriTimer);
    this.statoLavoriTimer = null;
  }

  registerPresence(pin: string, participantId: string): void {
    if (!this.socket) this.connect();
    const normalized = String(pin ?? '').trim().toUpperCase();
    if (!normalized || !participantId) return;
    // Phase 2a: track so reconnect handler can re-register.
    this.registeredPresence.set(normalized, participantId);
    this.startStatoLavoriBeat();
    const doEmit = () => {
      this.socket?.emit('register_presence', {
        pin: normalized,
        participant_id: participantId,
      });
      this.trace &&
        console.log(
          `[${this.logPrefix} Socket] register_presence:`,
          normalized,
          participantId,
        );
    };
    if (this.socket?.connected) {
      doEmit();
    } else {
      this.socket?.once('connect', doEmit);
    }
  }

  // ---- Event emission ----

  /**
   * Emit an event to the server.
   * Connects automatically if not already connected.
   */
  emit<K extends keyof import('./types').ClientEvents>(
    event: K,
    ...args: Parameters<import('./types').ClientEvents[K]>
  ): void {
    if (!this.socket) this.connect();
    const doEmit = () => {
      this.socket?.emit(event, ...args);
    };
    if (this.socket?.connected) {
      doEmit();
    } else {
      this.socket?.once('connect', doEmit);
    }
  }

  /**
   * Emit an event to a specific room (server-side broadcast).
   * This requires access to the server's io instance — only works
   * when called from server-side code. On the client, this is a no-op.
   * @deprecated Use emit() for client→server communication.
   */
  emitToRoom(_room: string, _event: string, _data: unknown): void {
    // Client cannot broadcast to rooms — only server can do this.
    // This method exists to prevent the fragile `(socketService as any).io` pattern.
    console.warn(
      `[${this.logPrefix} Socket] emitToRoom is server-only. ` +
        'Use emit() for client→server events.',
    );
  }

  // ---- Event listening ----

  /**
   * Listen to a server event.
   */
  on<K extends keyof import('./types').ServerEvents>(
    event: K,
    callback: import('./types').ServerEvents[K],
  ): void {
    const eventKey = event as string;
    const fn = callback as (...args: unknown[]) => void;
    let callbacks = this.listeners.get(eventKey);
    if (!callbacks) {
      callbacks = new Set();
      this.listeners.set(eventKey, callbacks);
    }
    if (callbacks.has(fn)) return;
    callbacks.add(fn);
    this.socket?.on(eventKey, fn);
  }

  /**
   * Stop listening to a server event.
   */
  off<K extends keyof import('./types').ServerEvents>(
    event: K,
    callback?: import('./types').ServerEvents[K],
  ): void {
    const eventKey = event as string;
    const callbacks = this.listeners.get(eventKey);
    if (!callbacks) return;

    // Solo gli handler dell'app: socket.off(event) senza callback staccherebbe
    // anche quelli interni (connect/disconnect/join_room_rejected).
    const toRemove = callback ? [callback as (...args: unknown[]) => void] : [...callbacks];
    for (const fn of toRemove) {
      callbacks.delete(fn);
      this.socket?.off(eventKey, fn);
    }
    if (callbacks.size === 0) this.listeners.delete(eventKey);
  }

  /**
   * Remove all listeners registered via on(). Internal handlers (reconnect
   * re-join, presence, identity loss) stay attached.
   */
  offAll(): void {
    for (const [event, callbacks] of this.listeners) {
      for (const fn of callbacks) this.socket?.off(event, fn);
    }
    this.listeners.clear();
  }

  // ---- Lifecycle callbacks ----

  /**
   * Register a callback invoked on every connect (including reconnects).
   * Useful for re-joining rooms after reconnection.
   *
   * @returns Unsubscribe function
   */
  onConnect(callback: () => void): () => void {
    this.onConnectCallbacks.push(callback);
    return () => {
      this.onConnectCallbacks = this.onConnectCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Register a callback invoked on disconnect.
   *
   * @returns Unsubscribe function
   */
  onDisconnect(callback: (reason: string) => void): () => void {
    this.onDisconnectCallbacks.push(callback);
    return () => {
      this.onDisconnectCallbacks = this.onDisconnectCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * WP-13e: invocata quando il server toglie l'identita' studente (disconnessione `io server disconnect`
   * o `join_room_rejected` con `identity_required`). Il token e' gia' stato cancellato: l'app deve
   * tornare all'ingresso (PIN/avatar), non ritentare.
   *
   * @returns Unsubscribe function
   */
  onIdentityLost(callback: (reason: 'revoked' | 'identity_required') => void): () => void {
    this.identityLostCallbacks.push(callback);
    return () => {
      this.identityLostCallbacks = this.identityLostCallbacks.filter((cb) => cb !== callback);
    };
  }

  // ---- Auth ----

  /**
   * Refresh the auth token on the current connection.
   * Useful when the token has been rotated.
   */
  refreshAuthToken(token?: string): void {
    const newToken = token ?? getAuthToken();
    if (newToken && this.socket) {
      // Socket.IO doesn't support changing auth after connection.
      // Reconnect with new token if needed.
      this.authConfig = { token: newToken, data: this.authConfig?.data };
      this.trace && console.log(`[${this.logPrefix} Socket] Auth token refreshed`);
    }
  }
}

/**
 * Factory function to create a SocketService instance.
 *
 * @example
 * // Basic usage (auto-connects on first emit/join)
 * const socket = createSocketService({ logPrefix: 'WordCloud' });
 * socket.joinRoom('ABC123');
 *
 * @example
 * // With auth and trace
 * const socket = createSocketService({
 *   logPrefix: 'QNS',
 *   trace: true,
 *   auth: { token: getAuthToken() },
 * });
 */
export function createSocketService(
  options: CreateSocketServiceOptions = {},
): SocketService {
  return new SocketService(options);
}
